/**
 * ZPL Label Builder
 *
 * Generates ZPL II commands for Zebra label printers.
 * Supports percentage-based positioning relative to label dimensions.
 * Produces both ZPL code and element arrays for HTML preview rendering.
 */
import type {
  BarcodeElement,
  BarcodeOptions,
  BoxElement,
  BoxOptions,
  FieldBlockElement,
  FieldBlockOptions,
  ImageElement,
  ImageOptions,
  InverseBoxElement,
  InverseBoxOptions,
  LabelElement,
  LineElement,
  LineOptions,
  QRCodeElement,
  QRCodeOptions,
  TextElement,
  TextOptions,
} from '../../types/printing';

export class ZPLLabelBuilder {
  private labelWidthDots: number;
  private labelHeightDots: number;
  private rotation: number;
  private zpl: string;
  private elements: LabelElement[];

  constructor(labelWidthDots: number, labelHeightDots: number, rotation = 0) {
    this.labelWidthDots = labelWidthDots;
    this.labelHeightDots = labelHeightDots;
    this.rotation = rotation;
    this.zpl = `^XA^PW${labelWidthDots}^LL${labelHeightDots}\n`;
    this.elements = [];
  }

  // --- Dimension helpers ---

  private processDimension(value: number | string, baseDimension: number): number {
    if (typeof value === 'string' && value.endsWith('%')) {
      const percent = Math.max(0, Math.min(100, parseFloat(value) || 0));
      return Math.round((baseDimension * percent) / 100);
    }
    return Number(value);
  }

  private processPosition(value: number | string, isX: boolean): number {
    const base = isX ? this.labelWidthDots : this.labelHeightDots;
    return this.processDimension(value, base);
  }

  private processFontSize(value: number | string): number {
    return this.processDimension(value, this.labelHeightDots);
  }

  private calculateRotatedPosition(x: number, y: number): { x: number; y: number } {
    if (this.rotation === 90) return { x: y, y: this.labelWidthDots - x };
    if (this.rotation === 180) return { x: this.labelWidthDots - x, y: this.labelHeightDots - y };
    if (this.rotation === 270) return { x: this.labelHeightDots - y, y: x };
    return { x, y };
  }

  // --- Element builders ---

  addText(text: string, options: TextOptions = {}): this {
    const x = this.processPosition(options.x || 0, true);
    const y = this.processPosition(options.y || 0, false);
    const { x: rx, y: ry } = this.calculateRotatedPosition(x, y);
    const fontSize = this.processFontSize(options.fontSize || 40);
    const fontType = options.fontType || '0';
    const rotationCode =
      this.rotation === 90 ? 'R' : this.rotation === 180 ? 'I' : this.rotation === 270 ? 'B' : 'N';

    this.zpl += `^FO${rx},${ry}^A${fontType}${rotationCode},${fontSize}^FD${text}^FS\n`;

    this.elements.push({
      type: 'text',
      x: rx,
      y: ry,
      text,
      fontSize,
      fontType,
      rotation: this.rotation,
      orientation: rotationCode,
      alignment: options.alignment || 'L',
    } as TextElement);

    return this;
  }

  addFieldBlock(data: string, options: FieldBlockOptions): this {
    const x = this.processPosition(options.x || 0, true);
    const y = this.processPosition(options.y || 0, false);
    const width = this.processPosition(options.width || '100%', true);
    const fontSize = this.processFontSize(options.fontSize || 40);
    const max = options.max || 1;
    const alignment = options.alignment || 'L';

    this.zpl += `^FO${x},${y}^FB${width},${max},0,${alignment},0^A0N,${fontSize},${fontSize}^FD${data}\\&^FS\n`;

    this.elements.push({
      type: 'fieldblock',
      x,
      y,
      width,
      data,
      fontSize,
      max,
      alignment,
    } as FieldBlockElement);

    return this;
  }

  addBarcode(data: string, options: BarcodeOptions = {}): this {
    const x = this.processPosition(options.x || 0, true);
    const y = this.processPosition(options.y || 0, false);
    const height = this.processDimension(options.height || '10%', this.labelHeightDots);
    const type = options.type || 'BC';
    const moduleWidth = options.moduleWidth || 2;
    const ratio = options.wideBarToNarrowBarWidthRatio || 2;
    const humanReadable = options.humanReadable || 'Y';

    this.zpl += `^FO${x},${y}^BY${moduleWidth},${ratio}\n`;
    this.zpl += `^${type}N,${height},${humanReadable},N,N^FD${data}^FS\n`;

    this.elements.push({
      type: 'barcode',
      x,
      y,
      data,
      height,
      barcodeType: type,
      moduleWidth,
      ratio,
      humanReadable,
    } as BarcodeElement);

    return this;
  }

  addQRCode(data: string, options: QRCodeOptions = {}): this {
    const x = this.processPosition(options.x || 0, true);
    const y = this.processPosition(options.y || 0, false);
    const model = options.model || 2;
    let magnificationFactor: number;

    if (options.magnificationFactor) {
      magnificationFactor = options.magnificationFactor;
    } else {
      const baseModuleCount = 21;
      const desiredSize = Math.min(this.labelWidthDots, this.labelHeightDots) * 0.2;
      magnificationFactor = Math.max(1, Math.min(Math.floor(desiredSize / baseModuleCount), 10));
    }

    const errorCorrection = options.errorCorrection || 'M';
    const maskValue = options.maskValue || 7;

    this.zpl += `^FO${x},${y}^BQN,${model},${magnificationFactor}\n`;
    this.zpl += `^FDLA,${data}^FS\n`;

    this.elements.push({
      type: 'qrcode',
      x,
      y,
      data,
      model,
      magnificationFactor,
      errorCorrection,
      maskValue,
    } as QRCodeElement);

    return this;
  }

  addImage(imageName: string, options: ImageOptions = {}): this {
    const x = this.processPosition(options.x || 0, true);
    const y = this.processPosition(options.y || 0, false);
    const scaleX = options.scaleX || 1;
    const scaleY = options.scaleY || 1;

    this.zpl += `^FO${x},${y}^XGR:${imageName},${scaleX},${scaleY}^FS\n`;

    this.elements.push({
      type: 'image',
      x,
      y,
      imageName,
      scaleX,
      scaleY,
    } as ImageElement);

    return this;
  }

  addBox(options: BoxOptions): this {
    const x = this.processPosition(options.x || 0, true);
    const y = this.processPosition(options.y || 0, false);
    const width = this.processDimension(options.width, this.labelWidthDots);
    const height = this.processDimension(options.height, this.labelHeightDots);
    const borderThickness = options.borderThickness || 1;
    const borderColor = options.borderColor || 'B';
    const cornerRounding = options.cornerRounding || 0;

    this.zpl += `^FO${x},${y}^GB${width},${height},${borderThickness},${borderColor},${cornerRounding}^FS\n`;

    this.elements.push({
      type: 'box',
      x,
      y,
      width,
      height,
      borderThickness,
      borderColor,
      cornerRounding,
    } as BoxElement);

    return this;
  }

  addInverseBox(options: InverseBoxOptions): this {
    const x = this.processPosition(options.x || 0, true);
    const y = this.processPosition(options.y || 0, false);
    const width = this.processDimension(options.width, this.labelWidthDots);
    const height = this.processDimension(options.height, this.labelHeightDots);
    const borderThickness = this.processDimension(
      options.borderThickness || 1,
      Math.min(this.labelWidthDots, this.labelHeightDots)
    );
    const borderColor = (options.borderColor || 'B') as string;
    const cornerRounding = this.processDimension(
      options.cornerRounding || 0,
      Math.min(this.labelWidthDots, this.labelHeightDots)
    );

    this.zpl += `^LRY^FO${x},${y}^GB${width},${height},${borderThickness},${borderColor},${cornerRounding}^FS^LRN\n`;

    this.elements.push({
      type: 'inverse-box',
      x,
      y,
      width,
      height,
      borderThickness,
      borderColor,
      cornerRounding,
    } as InverseBoxElement);

    return this;
  }

  addLine(options: LineOptions): this {
    const x = this.processPosition(options.x || 0, true);
    const y = this.processPosition(options.y || 0, false);
    const width = this.processDimension(options.width, this.labelWidthDots);
    const height = this.processDimension(options.height, this.labelHeightDots);
    const thickness = options.thickness || 1;

    this.zpl += `^FO${x},${y}^GB${width},${height},${thickness}^FS\n`;

    this.elements.push({
      type: 'line',
      x,
      y,
      width,
      height,
      thickness,
    } as LineElement);

    return this;
  }

  getZPL(): string {
    return this.zpl + '^XZ';
  }

  getElements(): LabelElement[] {
    return [...this.elements];
  }
}
